<template>
  <div>
    <header class="db-page-head">
      <div>
        <h2 class="db-page-title">Usuarios</h2>
        <p class="db-page-subtitle">Personas con acceso al panel de control y el rol de cada una.</p>
      </div>
      <div class="db-actions">
        <button type="button" class="db-btn" @click="abrirNuevo">
          <span class="material-symbols-outlined">person_add</span> Nuevo usuario
        </button>
      </div>
    </header>

    <section class="db-card">
      <div class="db-toolbar">
        <label class="db-search">
          <span class="material-symbols-outlined">search</span>
          <input v-model.trim="busqueda" type="search" placeholder="Buscar por nombre o correo…" aria-label="Buscar usuario" />
        </label>
        <select v-model="filtroRol" class="db-input gu-select" aria-label="Filtrar por rol">
          <option value="">Todos los roles</option>
          <option v-for="r in roles" :key="r.name" :value="r.name">{{ r.etiqueta }}</option>
        </select>
        <div class="db-segmented" role="group" aria-label="Filtrar por estado">
          <button
            v-for="f in filtrosEstado"
            :key="f.key"
            type="button"
            :class="{ 'is-active': filtroEstado === f.key }"
            @click="filtroEstado = f.key"
          >
            {{ f.texto }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="db-card-body gu-skeletons">
        <div v-for="n in 3" :key="n" class="db-skeleton" style="height: 52px"></div>
      </div>

      <div v-else-if="!filtrados.length" class="db-empty">
        <span class="material-symbols-outlined">group</span>
        <p>{{ usuarios.length ? "Ningún usuario coincide con los filtros." : "No hay usuarios." }}</p>
      </div>

      <div v-else class="db-table-wrap">
        <table class="db-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              <th class="db-hide-sm">Estado</th>
              <th class="db-hide-sm">Último acceso</th>
              <th class="db-col-actions"><span class="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in filtrados" :key="u.id">
              <td>
                <div class="gu-user">
                  <span class="gu-avatar" :class="{ 'is-super': u.es_superadmin }">{{ iniciales(u.name || u.email) }}</span>
                  <span>
                    <span class="db-cell-title">
                      {{ u.name }}
                      <span v-if="u.es_yo" class="db-badge db-badge--info">Tú</span>
                    </span>
                    <span class="db-cell-sub">{{ u.email }}</span>
                  </span>
                </div>
              </td>
              <td>
                <span class="db-badge" :class="u.es_superadmin ? 'db-badge--gold' : 'db-badge--info'">{{ u.rol_etiqueta }}</span>
              </td>
              <td class="db-hide-sm">
                <span v-if="u.bloqueado" class="db-badge db-badge--danger">
                  <span class="material-symbols-outlined">lock</span> Bloqueado
                </span>
                <span v-else-if="u.debe_cambiar_password" class="db-badge db-badge--warning">
                  <span class="material-symbols-outlined">key</span> Contraseña pendiente
                </span>
                <span v-else class="db-badge db-badge--success">
                  <span class="material-symbols-outlined">check_circle</span> Activo
                </span>
              </td>
              <td class="db-hide-sm gu-muted">{{ u.ultimo_acceso ? formatFechaHora(u.ultimo_acceso) : "Nunca" }}</td>
              <td class="db-col-actions">
                <div class="gu-actions">
                  <button
                    v-if="u.bloqueado && u.acciones.cambiar_acceso"
                    type="button"
                    class="db-icon-btn"
                    title="Desbloquear"
                    :aria-label="`Desbloquear a ${u.name}`"
                    @click="desbloquear(u)"
                  >
                    <span class="material-symbols-outlined">lock_open</span>
                  </button>
                  <button
                    v-if="u.acciones.cambiar_acceso"
                    type="button"
                    class="db-icon-btn"
                    title="Cerrar sus sesiones"
                    :aria-label="`Cerrar las sesiones de ${u.name}`"
                    @click="cerrarSesiones(u)"
                  >
                    <span class="material-symbols-outlined">logout</span>
                  </button>
                  <button
                    type="button"
                    class="db-icon-btn"
                    :disabled="!u.acciones.editar"
                    :title="u.acciones.editar ? 'Editar' : 'Solo un superadministrador puede editarlo'"
                    :aria-label="`Editar a ${u.name}`"
                    @click="abrirEditar(u)"
                  >
                    <span class="material-symbols-outlined">edit</span>
                  </button>
                  <button
                    type="button"
                    class="db-icon-btn db-icon-btn--danger"
                    :disabled="!u.acciones.eliminar"
                    :title="u.acciones.eliminar ? 'Eliminar' : u.es_yo ? 'No puedes eliminarte a ti mismo' : 'No puedes eliminar a este usuario'"
                    :aria-label="`Eliminar a ${u.name}`"
                    @click="eliminar(u)"
                  >
                    <span class="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <modal-usuario modal-id="modalUsuario" :usuario="seleccionado" :roles="roles" :permisos="permisos" @guardado="cargar" />
  </div>
</template>

<script>
import ModalUsuario from "@/components/dashboard/modals/ModalUsuario.vue";
import axios from "@/directives/axios.js";
import {
  avisar,
  confirmar,
  formatFechaHora,
  iniciales,
  normalizar,
  avisarError,
} from "@/directives/dbDialogs.js";

export default {
  name: "VistaGestorUsuarios",

  components: { ModalUsuario },

  data() {
    return {
      usuarios: [],
      roles: [],
      permisos: [],
      seleccionado: null,
      loading: true,
      busqueda: "",
      filtroRol: "",
      filtroEstado: "todos",
      filtrosEstado: [
        { key: "todos", texto: "Todos" },
        { key: "activos", texto: "Activos" },
        { key: "bloqueados", texto: "Bloqueados" },
      ],
    };
  },

  computed: {
    filtrados() {
      const q = normalizar(this.busqueda);
      return this.usuarios.filter((u) => {
        if (q && !normalizar(`${u.name} ${u.email}`).includes(q)) return false;
        if (this.filtroRol && u.rol !== this.filtroRol) return false;
        if (this.filtroEstado === "activos" && u.bloqueado) return false;
        if (this.filtroEstado === "bloqueados" && !u.bloqueado) return false;
        return true;
      });
    },
  },

  methods: {
    formatFechaHora,
    iniciales,

    async cargar() {
      try {
        const [usuarios, roles, permisos] = await Promise.all([
          axios.get("/usuarios"),
          axios.get("/roles"),
          axios.get("/permisos"),
        ]);
        this.usuarios = usuarios.data.usuarios || [];
        this.roles = roles.data.roles || [];
        this.permisos = permisos.data.permisos || [];
      } catch (error) {
        avisarError(this, error, "No se pudieron cargar los usuarios.");
      } finally {
        this.loading = false;
      }
    },

    abrirNuevo() {
      this.seleccionado = null;
      this.$nextTick(() => this.$bvModal.show("modalUsuario"));
    },

    abrirEditar(usuario) {
      this.seleccionado = usuario;
      this.$nextTick(() => this.$bvModal.show("modalUsuario"));
    },

    async desbloquear(usuario) {
      try {
        await axios.post(`/usuarios/${usuario.id}/desbloquear`);
        avisar(this, `«${usuario.name}» ya puede volver a iniciar sesión.`, "success", "Usuario desbloqueado");
      } catch (error) {
        avisarError(this, error, `No se pudo desbloquear a «${usuario.name}».`);
      } finally {
        await this.cargar();
      }
    },

    async cerrarSesiones(usuario) {
      const ok = await confirmar(this, `${usuario.name} tendrá que volver a iniciar sesión en todos sus dispositivos.`, {
        titulo: "Cerrar sesiones",
        ok: "Cerrar sesiones",
      });
      if (!ok) return;

      try {
        await axios.delete(`/usuarios/${usuario.id}/sesiones`);
        avisar(this, `«${usuario.name}» tendrá que volver a iniciar sesión.`, "success", "Sesiones cerradas");
      } catch (error) {
        avisarError(this, error, `No se pudieron cerrar las sesiones de «${usuario.name}».`);
      }
    },

    async eliminar(usuario) {
      const ok = await confirmar(this, `Se eliminará la cuenta de ${usuario.name} (${usuario.email}). Esta acción no se puede deshacer.`, {
        titulo: "Eliminar usuario",
        ok: "Eliminar",
        peligro: true,
      });
      if (!ok) return;

      try {
        await axios.delete(`/usuarios/${usuario.id}`);
        // Se quita de la tabla al momento y luego se sincroniza con el servidor
        this.usuarios = this.usuarios.filter((u) => u.id !== usuario.id);
        avisar(this, `Se ha eliminado la cuenta de «${usuario.name}» (${usuario.email}).`, "success", "Usuario eliminado");
      } catch (error) {
        avisarError(this, error, `No se pudo eliminar a «${usuario.name}».`);
      } finally {
        await this.cargar();
      }
    },
  },

  mounted() {
    this.cargar();
  },
};
</script>

<style scoped>
.gu-skeletons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gu-select {
  width: auto;
  min-width: 170px;
}

.gu-user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gu-avatar {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--sd-primary-soft);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--sd-primary);
}

.gu-avatar.is-super {
  background: var(--sd-gold-soft);
  color: var(--sd-gold);
}

.gu-muted {
  font-size: 0.85rem;
  color: var(--sd-muted);
}

.gu-actions {
  display: inline-flex;
  gap: 2px;
}
</style>
